import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import axios from 'axios';

const WaitlistPage = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total: 0, by_grade: [] });
  const [waitlistData, setWaitlistData] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [promoting, setPromoting] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchWaitlistByGrade(selectedGrade);
  }, [selectedGrade]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/enrollments/waitlist/summary');
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching waitlist summary:', error);
      toast.error('Failed to load waitlist data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlistByGrade = async (gradeId) => {
    try {
      const params = gradeId ? `?grade_id=${gradeId}` : '';
      const response = await axios.get(`/api/enrollments/waitlist${params}`);
      if (response.data.success) {
        setWaitlistData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    }
  };

  const handlePromote = async (enrollmentId) => {
    try {
      setPromoting(enrollmentId);
      const response = await axios.post(`/api/enrollments/${enrollmentId}/promote`);
      if (response.data.success) {
        toast.success('Student promoted from waitlist successfully');
        fetchData();
        fetchWaitlistByGrade(selectedGrade);
      } else {
        toast.error(response.data.message || 'Failed to promote student');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to promote student');
    } finally {
      setPromoting(null);
    }
  };

  const gradeOptions = summary.by_grade.map(g => ({
    label: `${g.grade_name} (${g.waitlist_count})`,
    value: g.grade_id
  }));

  const positionTemplate = (rowData) => (
    <Tag severity="info" value={`#${rowData.waitlist_position}`} />
  );

  const studentTemplate = (rowData) => (
    <div>
      <div className="font-semibold">{rowData.student?.full_name}</div>
      <div className="text-sm text-500">ID: {rowData.student?.id}</div>
    </div>
  );

  const gradeTemplate = (rowData) => (
    <div>
      <div>{rowData.grade?.name}</div>
      <div className="text-sm text-500">Capacity: {rowData.grade?.max_capacity || 'Unlimited'}</div>
    </div>
  );

  const dateTemplate = (rowData) => {
    const date = new Date(rowData.enrolled_at);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const actionTemplate = (rowData) => (
    <Button
      icon="pi pi-check"
      label="Promote"
      className="p-button-success p-button-sm"
      loading={promoting === rowData.id}
      onClick={() => handlePromote(rowData.id)}
      tooltip="Promote to active enrollment"
    />
  );

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-800 mb-2">Waitlist Management</h1>
        <p className="text-500">Manage students waiting for enrollment spots</p>
      </div>

      {/* Summary Cards */}
      <div className="grid mb-4">
        <div className="col-12 md:col-3">
          <Card className="text-center bg-indigo-50 border-left-3 border-indigo-500">
            <div className="text-500 text-sm mb-2">Total Waitlisted</div>
            <div className="text-4xl font-bold text-indigo-600">{summary.total}</div>
          </Card>
        </div>
        <div className="col-12 md:col-9">
          <Card>
            <h3 className="text-lg font-semibold mb-3">Waitlist by Grade</h3>
            <div className="flex flex-wrap gap-2">
              {summary.by_grade.length === 0 ? (
                <p className="text-500">No students on waitlist</p>
              ) : (
                summary.by_grade.map(grade => (
                  <Tag
                    key={grade.grade_id}
                    className="cursor-pointer"
                    severity={selectedGrade === grade.grade_id ? 'info' : 'secondary'}
                    onClick={() => setSelectedGrade(
                      selectedGrade === grade.grade_id ? null : grade.grade_id
                    )}
                  >
                    {grade.grade_name}: {grade.waitlist_count}
                  </Tag>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Waitlist Table */}
      <Card>
        <div className="flex justify-content-between align-items-center mb-3">
          <h3 className="text-xl font-semibold m-0">
            {selectedGrade
              ? `Waitlist - ${summary.by_grade.find(g => g.grade_id === selectedGrade)?.grade_name}`
              : 'All Waitlisted Students'
            }
          </h3>
          <Dropdown
            value={selectedGrade}
            options={[{ label: 'All Grades', value: null }, ...gradeOptions]}
            onChange={(e) => setSelectedGrade(e.value)}
            placeholder="Filter by Grade"
            className="w-15rem"
          />
        </div>

        <DataTable
          value={waitlistData}
          paginator
          rows={10}
          emptyMessage="No students on waitlist"
          className="p-datatable-sm"
        >
          <Column
            header="Position"
            body={positionTemplate}
            sortable
            sortField="waitlist_position"
            style={{ width: '100px' }}
          />
          <Column
            header="Student"
            body={studentTemplate}
          />
          <Column
            header="Grade"
            body={gradeTemplate}
          />
          <Column
            header="Session"
            field="session.name"
          />
          <Column
            header="Waitlisted On"
            body={dateTemplate}
            sortable
            sortField="enrolled_at"
          />
          <Column
            header="Action"
            body={actionTemplate}
            style={{ width: '150px' }}
          />
        </DataTable>
      </Card>
    </div>
  );
};

export default WaitlistPage;
